// The address is a contract (02-DESIGN/03-console/01-the-console.md § the
// address): `/` is the sign-in page and the callback, `/<slug>` a workspace's
// Home, `/<slug>/<page>` one of its pages, with the slug as the sign-in hint.
// A slug begins with a letter, holds lowercase letters, digits and hyphens,
// and is not `auth`, the word the origin's other paths need, nor the name of
// one of the gateway's faces — `mcp`, `openai`, `anthropic`, `jev` — since a
// slug in front of a face path is that workspace's OAuth face
// (02-DESIGN/09-gateway/02-the-oauth-face.md § the address). The shape
// lives in the library and is used from there. The address keeps the
// designs' words where the page does not: the slug is in the address bar and
// on Settings, and nowhere else a person reads.

import { subject } from "@impire-io/sdk";

/** The pages under a workspace's address, as the design names them. */
export const PAGES = [
  "approvals",
  "apps",
  "toolkits",
  "workloads",
  "solutions",
  "people",
  "workspaces",
  "models",
  "configuration",
  "guardrails",
  "stores",
  "secrets",
  "audit",
  "settings",
  "me/activity",
  "me/credentials",
] as const;

export type Page = (typeof PAGES)[number];

/** What a page is called in the rail and as its title. */
export const PAGE_TITLES: Record<Page, string> = {
  approvals: "Approvals",
  apps: "Apps",
  toolkits: "Toolkits",
  workloads: "Workloads",
  solutions: "Solutions",
  people: "People",
  workspaces: "Workspaces",
  models: "Models",
  configuration: "Configuration",
  guardrails: "Guardrails",
  stores: "Stores",
  secrets: "Secrets",
  audit: "Audit & usage",
  settings: "Settings",
  "me/activity": "Your activity",
  "me/credentials": "Your credentials",
};

/**
 * RENAMED are the former addresses the amendment renamed (§ the address: "the
 * former `provider` and `sign-ins` are `sign-in` and `me/activity`
 * renamed"); a link to one goes to its new address, so a bookmark survives.
 */
export const RENAMED: Readonly<Record<string, Page>> = {
  provider: "settings",
  "sign-in": "settings",
  "sign-ins": "me/activity",
};

/**
 * RENAMED_TABS are the tabs a former address opens on its new page: the
 * Sign-in page is Settings' *Sign-in* tab (issue 182; § Settings: the
 * Sign-in tab).
 */
export const RENAMED_TABS: Readonly<Record<string, string>> = {
  provider: "sign-in",
  "sign-in": "sign-in",
};

export function isPage(s: string): s is Page {
  return (PAGES as readonly string[]).includes(s);
}

/**
 * FACES are the gateway's face names, which a slug may not be: the address
 * `<gateway>/<slug>/<face>/…` is a workspace's OAuth face, so a workspace
 * named for a face would be read as one. sdk-js v0.8.0's `validSlug` carries
 * the same rule (its `RESERVED_SLUGS`); until the console pins it, the list
 * is kept here too.
 */
export const FACES = ["mcp", "openai", "anthropic", "jev"] as const;

/**
 * reservedWhy says why a word that has a slug's shape is still not a
 * workspace's address, in the page's words; undefined for any other word.
 */
export function reservedWhy(s: string): string | undefined {
  if (s === "auth") {
    return "`auth` is the sign-in service's own path, not a workspace";
  }
  if ((FACES as readonly string[]).includes(s)) {
    return `\`${s}\` is the name of one of the gateway's ways in, not a workspace`;
  }
  return undefined;
}

/** validSlug is the design's rule: the library's shape, and none of the reserved words. */
export function validSlug(s: string): boolean {
  return subject.validSlug(s) && reservedWhy(s) === undefined;
}

/** Address is a parsed pathname. */
export type Address =
  | { kind: "neutral" }
  | { kind: "tenant"; slug: string; page: Page | null; rest: string[] }
  | { kind: "renamed"; slug: string; to: Page; tab?: string }
  | { kind: "invalid"; path: string; reason: string };

/** parseAddress reads a pathname. A trailing slash and empty segments are ignored. */
export function parseAddress(pathname: string): Address {
  const segments = pathname.split("/").filter((s) => s !== "");
  const first = segments[0];
  if (first === undefined) {
    return { kind: "neutral" };
  }
  if (!validSlug(first)) {
    return {
      kind: "invalid",
      path: pathname,
      reason:
        reservedWhy(first) ??
        `"${first}" is not a workspace's address: lowercase letters, digits and hyphens, beginning with a letter`,
    };
  }
  const second = segments[1];
  if (second === undefined) {
    return { kind: "tenant", slug: first, page: null, rest: [] };
  }
  const renamed = RENAMED[second];
  if (renamed !== undefined) {
    const tab = RENAMED_TABS[second];
    return tab === undefined
      ? { kind: "renamed", slug: first, to: renamed }
      : { kind: "renamed", slug: first, to: renamed, tab };
  }
  if (second === "me") {
    const own = `me/${segments[2] ?? ""}`;
    if (!isPage(own)) {
      return { kind: "invalid", path: pathname, reason: `"${own}" is not a page of the console` };
    }
    return { kind: "tenant", slug: first, page: own, rest: segments.slice(3) };
  }
  if (!isPage(second)) {
    return { kind: "invalid", path: pathname, reason: `"${second}" is not a page of a workspace's console` };
  }
  return { kind: "tenant", slug: first, page: second, rest: segments.slice(2) };
}

/** tenantPath builds `/<slug>`, `/<slug>/<page>` or `/<slug>/<page>/<rest…>`. */
export function tenantPath(slug: string, page?: Page, ...rest: string[]): string {
  const parts = [slug];
  if (page !== undefined) {
    parts.push(...page.split("/"), ...rest);
  }
  return "/" + parts.map(encodeURIComponent).join("/");
}

/**
 * slugHint reduces what a person typed on the sign-in page to a slug, or
 * null: a slug as typed, or an email reduced to its domain's first label.
 * The hint proves nothing; the profile read next says whether such a
 * workspace exists.
 */
export function slugHint(typed: string): string | null {
  const t = typed.trim().toLowerCase();
  if (t === "") {
    return null;
  }
  const at = t.indexOf("@");
  if (at >= 0) {
    const domain = t.slice(at + 1);
    const label = domain.split(".")[0] ?? "";
    return validSlug(label) ? label : null;
  }
  return validSlug(t) ? t : null;
}

const LOOPBACK = new Set(["localhost", "127.0.0.1", "[::1]"]);

/**
 * followable accepts an address the console sends the browser to off its own
 * origin — a provider's authorization or end-session endpoint, which a
 * profile or a toolkit advertises — only when it is an `https` URL with a
 * host, or `http` on a loopback host for development. A `javascript:` or
 * `data:` address would run in the console's origin, beside the session.
 */
export function followable(url: string): boolean {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return false;
  }
  if (u.hostname === "") {
    return false;
  }
  return u.protocol === "https:" || (u.protocol === "http:" && LOOPBACK.has(u.hostname));
}

/**
 * safeReturnTo accepts an address to return to after sign-in only when it is
 * a path at this origin: it begins with one slash and is not scheme-relative.
 */
export function safeReturnTo(path: string | null | undefined): string {
  if (path === null || path === undefined || path === "") {
    return "/";
  }
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) {
    return "/";
  }
  return path;
}
